import * as _ from "lodash";
import { DataStore } from "./data-store";
import { IoDrug, IoDiagnosis, IoDrugGroup, IoEffect, IoDrugTrademark, IoDrugSubstance, Effect, DrugSubstance, DrugTrademark, Diagnosis, DrugGroup, Probability, LinkedIoEffect } from "../../types";
import { namedThingsComparer, getSortedIndexItemComparer } from "../../../../data/compiler/util";

export class DrugDataStore {
    public copyright = "Copyright Freia Hünig 2019. All rights reserved."; // will be part of data export
    public static DrugType = "Drug";
    public static DiagnosisType = "Diagnosis";
    public static GroupType = "Group";

    public static hasEffectRelation = "HAS_EFFECT";
    public static hasDrugRelation = "HAS_DRUG";
    public static hasTrademarkRelation = "HAS_TRADEMARK";
    public static hasSubstanceRelation = "HAS_SUBSTANCE";
    public static hasDiagnosisRelation = "HAS_DIAGNOSIS";
    public static belongsToGroupRelation = "BELONGS_TO_GROUP";

    constructor(public ds: DataStore) { }

    public setDataStore(ds: DataStore) {
        this.ds = ds;
    }

    public getAllDrugs(): IoDrug[] {
        return this.ds.getIndex<IoDrug>(DrugDataStore.DrugType);
    }

    public getAllDiagnoses(): IoDiagnosis[] {
        return this.ds.getIndex<IoDiagnosis>(DrugDataStore.DiagnosisType);
    }

    public getAllGroups(): IoDrugGroup[] {
        return this.ds.getIndex<IoDrugGroup>(DrugDataStore.GroupType);
    }

    public getGroupById(id: string | number): IoDrugGroup {
        return this.ds.get(id);
    }

    public getDiagnosisById(id: string | number): IoDiagnosis {
        return this.ds.get(id);
    }

    public getGroupsOfDrug(drug: IoDrug): Array<IoDrugGroup> {
        return this.ds.getManyRelated<IoDrugGroup>(drug, DrugDataStore.belongsToGroupRelation);
    }

    public getDrugsOfGroup(group: IoDrugGroup): Array<IoDrug> {
        return this.ds.getManyRelated<IoDrug>(group, DrugDataStore.hasDrugRelation);
    }

    public getEffectsOfDrug(drug: IoDrug) {
        return this.ds.getManyRelated<IoEffect>(drug, DrugDataStore.hasEffectRelation);
    }

    public getLinkedEffectsOfDrug(drug: IoDrug) {
        return this.getAsLinkedEffects(this.getEffectsOfDrug(drug));
    }

    public getEffectsOfDiagnosis(diagnosis: IoDiagnosis) {
        return this.ds.getManyRelated<IoEffect>(diagnosis, DrugDataStore.hasEffectRelation);
    }

    public getLinkedEffectsOfDiagnosis(diagnosis: IoDiagnosis) {
        return this.getAsLinkedEffects(this.ds.getManyRelated<IoEffect>(diagnosis, DrugDataStore.hasEffectRelation));
    }

    public getDrugOfEffect(effect: IoEffect): IoDrug {
        return this.ds.getSingleRelated<IoDrug>(effect, DrugDataStore.hasDrugRelation);
    }

    public getDiagnosisOfEffect(effect: IoEffect): IoDiagnosis {
        return this.ds.getSingleRelated<IoDiagnosis>(effect, DrugDataStore.hasDiagnosisRelation);
    }

    public getSubstanceOfTrademark(trademark: IoDrugTrademark) {
        return this.ds.getSingleRelated<IoDrugSubstance>(trademark, DrugDataStore.hasSubstanceRelation);
    }

    public getTrademarksOfSubstance(substance: IoDrugSubstance) {
        return this.ds.getManyRelated<IoDrugTrademark>(substance, DrugDataStore.hasTrademarkRelation);
    }

    public sortRelatedEffects(relatingType: string, comparer: (a: IoEffect, b: IoEffect) => number) {
        this.ds.getIndex(relatingType).forEach(obj => {
            this.ds.sortRelation<IoEffect>(obj, DrugDataStore.hasEffectRelation, comparer);
        });
    }

    public sortIndexes() {
        this.ds.sortIndex(DrugDataStore.DrugType, namedThingsComparer);
        this.ds.sortIndex(DrugDataStore.DiagnosisType, namedThingsComparer);
        this.ds.sortIndex(DrugDataStore.GroupType, namedThingsComparer);
    }

    public sortRelationsToNamedThings() {
        this.ds.sortRelations(DrugDataStore.belongsToGroupRelation, getSortedIndexItemComparer());
        this.ds.sortRelations(DrugDataStore.hasDiagnosisRelation, getSortedIndexItemComparer());
        this.ds.sortRelations(DrugDataStore.hasDrugRelation, getSortedIndexItemComparer());
        this.ds.sortRelations(DrugDataStore.hasSubstanceRelation, getSortedIndexItemComparer());
        this.ds.sortRelations(DrugDataStore.hasTrademarkRelation, getSortedIndexItemComparer());
    }

    public relateEffectAndDrug(effect: IoEffect, drug: IoDrug) {
        this.ds.relateOneToSome(effect, DrugDataStore.hasDrugRelation, drug);
        this.ds.relateOneToSome(drug, DrugDataStore.hasEffectRelation, effect);
    }

    public relateEffectAndDiagnosis(effect: IoEffect, diagnosis: IoDiagnosis) {
        this.ds.relateOneToSome(effect, DrugDataStore.hasDiagnosisRelation, diagnosis);
        this.ds.relateOneToSome(diagnosis, DrugDataStore.hasEffectRelation, effect);
    }

    public relateDrugAndGrup(drug: IoDrugGroup, group: IoDrugGroup) {
        this.ds.relateOneToSome(drug, DrugDataStore.belongsToGroupRelation, group);
        this.ds.relateOneToSome(group, DrugDataStore.hasDrugRelation, drug);
    }

    public relateTrademarkAndSubstance(trademark: IoDrugTrademark, substance: IoDrugSubstance) {
        this.ds.relateOneToSome(trademark, DrugDataStore.hasSubstanceRelation, substance);
        this.ds.relateOneToSome(substance, DrugDataStore.hasTrademarkRelation, trademark);
    }

    public addEffect(effect: Effect): IoEffect {
        return this.ds.add(effect);
    }

    public addDrugSubstance(drug: DrugSubstance): IoDrugSubstance {
        return this.ds.add(drug, DrugDataStore.DrugType);
    }

    public addDrugTrademark(drug: DrugTrademark): IoDrugTrademark {
        return this.ds.add(drug, DrugDataStore.DrugType);
    }

    public addDiagnosis(diagnosis: Diagnosis): IoDiagnosis {
        return this.ds.add(diagnosis, DrugDataStore.DiagnosisType);
    }

    public addGroup(group: DrugGroup): IoDrugGroup {
        return this.ds.add(group, DrugDataStore.GroupType);
    }

    private getAsLinkedEffects(effects: Array<IoEffect>): Array<LinkedIoEffect> {
        return effects.map(e => {
            return <LinkedIoEffect>{
                id: e.id, // required for js-search
                probability: e.probability,
                drug: this.getDrugOfEffect(e),
                diagnosis: this.getDiagnosisOfEffect(e)
            }
        });
    }

    public calculateEffectStatsWhereMissing(drugs: Array<IoDrug>) {
        drugs.forEach(d => {
            if (d.effectStats) return;
            d.effectStats = this.calculateEffectStatsForDrug(d);
        });
    }

    private calculateEffectStatsForDrug(drug: IoDrug): [number, number, number] {
        const allEffects = this.getEffectsOfDrug(drug)
        const effects = allEffects.filter(e => isRealDiagnosis(this.getDiagnosisOfEffect(e)));
        const countHigh = effects.filter(e => [Probability.High, Probability.VeryHigh, Probability.VeryHighOrHigh].includes(e.probability)).length;
        const countModerate = effects.filter(e => [Probability.Moderate].includes(e.probability)).length;
        const countLow = effects.length - countHigh - countModerate;
        return [countHigh, countModerate, countLow];
    }
}

function isRealDiagnosis(diagnosis: Diagnosis) {
    return !_.startsWith(diagnosis.name, "Bislang keine weiteren UAW")
        && !_.startsWith(diagnosis.name, "Keine UAW an der Haut bekannt")
        && !_.startsWith(diagnosis.name, "Keine UAW bekannt");
}


// WEBPACK FOOTER //
// ./src/providers/data/drug-data-store.ts